<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { entityApi } from '../../Entity/api/entityApi';
import { contactsApi } from '../api/contactsApi';
import { useDatePicker } from '@/shared/composables/useDatePicker';
import type { CreateCorporateData, Contact } from '../types/contact.types';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { resolveContactDetailFieldReadonly } from '../utils/contactMyDetailEditPolicy';
import type { EntityOfficerContactOverlay } from '@features/Entity_my/types/entity.types';
import {
    CORPORATE_ID_TYPE_OPTIONS,
    parseCorporateIdTypeLabel,
    buildCorporateIdTypeJson,
} from '@/utils/corporateIdType';

interface Props {
    modelValue?: CreateCorporateData | Contact;
    readonly?: boolean;
    readonlyFields?: string[];
    /** MY Detail: 이름만 readonly. Create(Registrable Corporate)에서는 false */
    detailMode?: boolean;
    /** Entity Officer 모달 — L_OFFICER.FIRM_NO (Auditor) */
    entityOfficerOverlay?: EntityOfficerContactOverlay | null;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    readonlyFields: () => [],
    detailMode: false,
    entityOfficerOverlay: null,
});

const emit = defineEmits<{
    'update:modelValue': [value: CreateCorporateData | Contact];
    'update:entityOfficerOverlay': [value: EntityOfficerContactOverlay];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
const sessionStore = useSessionStore();

/** Entity Officer Auditor 모달에서만 Firm No. 표시 (L_OFFICER.FIRM_NO) */
const showEntityOfficerAuditorFirmNo = computed(
    () => props.entityOfficerOverlay?.positionGroup === 'auditor',
);

function formatAuditFirmNoDisplay(raw: string | undefined | null): string {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (/^AF/i.test(s)) return s;
    return `AF${s}`;
}

function patchEntityOfficerOverlay(patch: Partial<EntityOfficerContactOverlay>) {
    if (!props.entityOfficerOverlay) return;
    emit('update:entityOfficerOverlay', {
        ...props.entityOfficerOverlay,
        ...patch,
    });
}

// snake_case 필드로 변환하여 사용
const basicData = computed({
    get: () => {
        const value = props.modelValue || {} as any;
        
        // REPRESENTATIVE_CD는 null일 수 있으므로 명시적으로 처리
        const representativeCd = value.REPRESENTATIVE_CD ?? value.representativeCd ?? null;
        const representativeName = value.REPRESENTATIVE_NAME || value.representativeName || '';
        
        // snake_case 필드 우선 사용, 없으면 camelCase 사용, Create 페이지의 필드명도 지원
        // AUDITTING 컬럼 우선 사용, 없으면 AUDITOR_YN 사용 (하위 호환성)
        const auditingValue = value.AUDITTING !== undefined 
            ? (value.AUDITTING === 1 || value.AUDITTING === true ? 'Yes' : 'No')
            : (value.AUDITOR_YN !== undefined 
                ? (value.AUDITOR_YN === 1 || value.AUDITOR_YN === true || value.AUDITOR_YN === 'Yes' ? 'Yes' : 'No')
                : (value.auditorYn !== undefined
                    ? (value.auditorYn === 'Yes' ? 'Yes' : 'No')
                    : (value.publicAccountingEntities !== undefined
                        ? (value.publicAccountingEntities === 'Yes' ? 'Yes' : 'No')
                        : 'No')));
        
        const rawJur = value.JURISDICTION ?? value.jurisdiction;
        const jurNormalized =
            rawJur == null || String(rawJur).trim() === '' ? null : String(rawJur);

        return {
            JURISDICTION: jurNormalized,
            CONTACT_NAME: value.CONTACT_NAME || value.contactName || value.entityName || '',
            AUDITOR_YN: auditingValue,
            CONTACT_REG_NO: value.CONTACT_REG_NO || value.contactRegNo || value.registrationNumber || '',
            DATE_OF_INCORPORATION: value.DATE_OF_INCORPORATION || value.dateOfIncorporation || value.incorporationDate || '',
            REPRESENTATIVE_CD: representativeCd === '' || representativeCd === undefined ? null : representativeCd,
            REPRESENTATIVE_NAME: representativeName,
            PROFILE_FIRM_NO: value.PROFILE_FIRM_NO || value.profileFirmNo || value.FirmNo || '',
        };
    },
    set: (value) => {
        if (props.readonly) return;
        // snake_case로 변환하여 emit
        const currentModelValue = (props.modelValue || {}) as CreateCorporateData | Contact;
        // Convert 'Yes'/'No' to boolean for auditorYn
        const auditorYnBoolean = value.AUDITOR_YN === 'Yes' ? true : (value.AUDITOR_YN === 'No' ? false : undefined);
        const updated = {
            ...currentModelValue,
            JURISDICTION: value.JURISDICTION,
            jurisdiction: value.JURISDICTION, // Create 페이지용
            CONTACT_NAME: value.CONTACT_NAME,
            contactName: value.CONTACT_NAME,
            entityName: value.CONTACT_NAME, // Create 페이지용
            AUDITOR_YN: value.AUDITOR_YN,
            auditorYn: auditorYnBoolean,
            publicAccountingEntities: value.AUDITOR_YN, // Create 페이지용
            CONTACT_REG_NO: value.CONTACT_REG_NO,
            contactRegNo: value.CONTACT_REG_NO,
            registrationNumber: value.CONTACT_REG_NO, // Create 페이지용
            DATE_OF_INCORPORATION: value.DATE_OF_INCORPORATION,
            dateOfIncorporation: value.DATE_OF_INCORPORATION,
            incorporationDate: value.DATE_OF_INCORPORATION, // Create 페이지용
            REPRESENTATIVE_CD: value.REPRESENTATIVE_CD,
            representativeCd: value.REPRESENTATIVE_CD,
            representativeName: value.REPRESENTATIVE_CD, // Create 페이지용
            PROFILE_FIRM_NO: value.PROFILE_FIRM_NO,
            profileFirmNo: value.PROFILE_FIRM_NO,
        } as CreateCorporateData | Contact;
        emit('update:modelValue', updated);
    }
});

// 법인 ID Type (MY) — 저장은 ID_TYPE(JSON: {"TypeOfIdentificationOfShareholder":"..."}), 화면은 라벨
const corporateIdTypeOptions = CORPORATE_ID_TYPE_OPTIONS;
const corporateIdTypeLabel = computed(() => parseCorporateIdTypeLabel((props.modelValue as any)?.ID_TYPE));
function setCorporateIdType(label: unknown) {
    if (props.readonly) return;
    const updated = { ...(props.modelValue || {}), ID_TYPE: buildCorporateIdTypeJson(label) } as any;
    emit('update:modelValue', updated);
}

// Incorporation Date - useDatePicker 사용 (basicData 이후에 정의)
const { dateValue: incorporationDate, displayValue: incorporationDateDisplay, menuOpen: incorporationDateMenu, calendarDate: incorporationDateCalendarDate } = useDatePicker(
    computed(() => {
        const value = props.modelValue || {} as any;
        return value.DATE_OF_INCORPORATION || value.dateOfIncorporation || value.incorporationDate || '';
    }),
    true // 달력 열 때 오늘 날짜로 설정
);

// incorporationDate가 변경되면 basicData 업데이트
watch(incorporationDate, (newValue) => {
    if (props.readonly || props.readonlyFields.includes('DATE_OF_INCORPORATION')) return;
    const currentValue = props.modelValue || {} as any;
    const updated = {
        ...currentValue,
        DATE_OF_INCORPORATION: newValue || '',
        dateOfIncorporation: newValue || '',
        incorporationDate: newValue || '',
    };
    emit('update:modelValue', updated);
});

// props.modelValue가 변경되면 incorporationDate 업데이트 (Detail 페이지에서 데이터 로드 시)
watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        const dateValue = (newValue as any).DATE_OF_INCORPORATION || (newValue as any).dateOfIncorporation || (newValue as any).incorporationDate || '';
        if (dateValue && dateValue !== incorporationDate.value) {
            incorporationDate.value = dateValue;
        }
        
        // Detail 페이지에서 데이터가 로드된 후, 현재 선택된 REPRESENTATIVE_CD가 옵션 목록에 있는지 확인
        if (representativeOptions.value.length > 0) {
            ensureCurrentRepresentativeInOptions();
        }
    }
}, { deep: true });

onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
    // Jurisdiction 옵션 로드
    loadJurisdictions();
    // Representative Name 옵션 로드
    loadRepresentatives();
});

// Validation Rules
const entityNameRules = ref([
    (v: string) => !!v || (t('contact.Entity Name') || 'Entity Name') + ' ' + (t('contact.is required') || 'is required'),
    (v: string) => {
        if (!v) return true;
        const trimmed = v.trim();
        if (trimmed.length < 2) return (t('contact.Entity Name') || 'Entity Name') + ' ' + (t('contact.must be at least 2 characters') || 'must be at least 2 characters');
        if (trimmed.length > 100) return (t('contact.Entity Name') || 'Entity Name') + ' ' + (t('contact.must be at most 100 characters') || 'must be at most 100 characters');
        return true;
    },
]);

const publicAccountingEntitiesRules = ref([
    (v: string) => {
        if (!v || (v !== 'Yes' && v !== 'No')) {
            return (t('contact.Public Accounting Entities') || 'Public Accounting Entities') + ' ' + (t('contact.must be selected') || 'must be selected');
        }
        return true;
    },
]);

// Registration Number validation 제거 (빈 값 허용)
const registrationNumberRules = ref([]);

// Jurisdiction 목록 (B15, CM) - EntityDetailPage처럼
const jurisdictionOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

// Jurisdiction 로드 (B15, CM) - EntityInfoSection.vue와 동일한 방식
const loadJurisdictions = async () => {
    try {
        // 언어 파라미터를 undefined로 전달하여 모든 언어 조회 (언어 필터링 우회)
        const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        
        console.log('🔍 [loadJurisdictions] Raw codes from API:', codes.length, codes);
        
        // DEFAULT_CODE_NAME 우선 사용, 없으면 CODE_NAME 사용
        jurisdictionOptions.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim()
            }))
            .filter((item: any) => {
                return item.TOTAL_CODE && item.CODE_NAME && 
                       item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0;
            });
        
        // TOTAL_CODE 기준으로 중복 제거 (같은 TOTAL_CODE가 여러 언어로 있을 수 있음)
        const uniqueCodesMap = new Map();
        jurisdictionOptions.value.forEach((item: any) => {
            if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
                uniqueCodesMap.set(item.TOTAL_CODE, item);
            }
        });
        
        const allOptions = Array.from(uniqueCodesMap.values());
        
        // SUB_CODE가 "000"인 항목(마지막 3자리가 "000")을 "Please Select"로 변경
        const processedOptions = allOptions.map((item: any) => {
            const subCode = item.TOTAL_CODE.slice(-3);
            if (subCode === '000') {
                return {
                    CODE_NAME: 'Please Select',
                    TOTAL_CODE: ''
                };
            }
            return item;
        });
        
        jurisdictionOptions.value = processedOptions.filter((item: any) => {
            return item.CODE_NAME && item.CODE_NAME.length > 0;
        });
        
        console.log('🔍 [loadJurisdictions] Jurisdiction (B15) 로드 완료:', jurisdictionOptions.value.length, '개');
        console.log('🔍 [loadJurisdictions] Jurisdiction 목록:', jurisdictionOptions.value.map((item: any) => ({ TOTAL_CODE: item.TOTAL_CODE, CODE_NAME: item.CODE_NAME })));
    } catch (error) {
        console.error('❌ [loadJurisdictions] Jurisdiction 로드 실패:', error);
        jurisdictionOptions.value = [];
    }
};

// Representative Name 목록 (Contact 목록에서 가져옴) - EntityMainContactSection처럼
const representativeOptions = ref<Array<{ CONTACT_CD: string; CONTACT_NAME: string }>>([]);

// Representative Name 목록 로드 (EntityCreatePage의 loadContacts처럼)
const loadRepresentatives = async () => {
    try {
        const response = await entityApi.getContactsList();
        const responseData = (response as any).data || response;
        const contacts = responseData.data?.contacts || responseData.contacts || [];
        
        const mappedContacts = contacts
            .filter((contact: any) => {
                // Individual만 필터링 (CATEGORY_CD = 'B09002')
                const categoryCd = contact.CATEGORY_CD || contact.categoryCd || '';
                return categoryCd === 'B09002';
            })
            .map((contact: any) => ({
                CONTACT_CD: String(contact.CONTACT_CD || contact.contactCd || '').trim(),
                CONTACT_NAME: String(contact.CONTACT_NAME || contact.contactName || '').trim(),
            }))
            .filter((item: any) => {
                return item.CONTACT_CD && item.CONTACT_NAME && item.CONTACT_CD.length > 0 && item.CONTACT_NAME.length > 0;
            });
        
        representativeOptions.value = mappedContacts;
        
        // Detail 페이지에서 현재 선택된 REPRESENTATIVE_CD가 옵션 목록에 없으면 추가
        await ensureCurrentRepresentativeInOptions();
    } catch (error) {
        console.error('❌ [loadRepresentatives] Failed to load representatives:', error);
        representativeOptions.value = [];
    }
};

// 현재 선택된 REPRESENTATIVE_CD가 옵션 목록에 있는지 확인하고 없으면 추가
const ensureCurrentRepresentativeInOptions = async () => {
    const currentValue = props.modelValue || {} as any;
    const representativeCd = currentValue.REPRESENTATIVE_CD ?? currentValue.representativeCd ?? null;
    
    // null, undefined, 빈 문자열이면 스킵
    if (!representativeCd || (typeof representativeCd === 'string' && representativeCd.trim() === '')) {
        return;
    }
    
    const representativeCdStr = String(representativeCd).trim();
    
    // 이미 옵션 목록에 있는지 확인
    const exists = representativeOptions.value.some(
        option => option.CONTACT_CD === representativeCdStr
    );
    
    if (!exists) {
        // 옵션 목록에 없으면 Contact 정보를 가져와서 추가
        try {
            const response = await contactsApi.getContactById(representativeCdStr);
            const responseData = (response as any).data || response;
            const contact = responseData.contact || responseData;
            
            if (contact) {
                const contactCd = String(contact.CONTACT_CD || contact.contactCd || '').trim();
                const contactName = String(contact.CONTACT_NAME || contact.contactName || '').trim();
                
                if (contactCd && contactName) {
                    // 옵션 목록에 추가 (맨 앞에 추가하여 현재 선택된 항목이 보이도록)
                    representativeOptions.value = [
                        { CONTACT_CD: contactCd, CONTACT_NAME: contactName },
                        ...representativeOptions.value
                    ];
                    console.log('✅ [ensureCurrentRepresentativeInOptions] Added current representative to options:', { CONTACT_CD: contactCd, CONTACT_NAME: contactName });
                }
            }
        } catch (error) {
            console.error('❌ [ensureCurrentRepresentativeInOptions] Failed to load current representative:', error);
            // 에러가 발생해도 기존 옵션 목록은 유지
        }
    }
};

const isJurisdictionMY = computed(() => {
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = customerInfo?.jurisdiction || '';
    const u = jurisdiction?.toUpperCase();
    return u === 'MY' || u === 'MALAYSIA';
});

/** MY Contact Detail: 이름만 readonly (Create에서는 Entity Name 편집 가능) */
const isMyDetailEditableExceptName = computed(
    () => isJurisdictionMY.value && props.detailMode,
);

// REGISTER_TYPE이 A06001인지 확인
const isBizFileRegistrant = computed(() => {
    const value = props.modelValue || {} as any;
    const registerType = String(value.CONTACT_REGISTER_TYPE || value.contactRegisterType || '').trim().toUpperCase();
    return registerType === 'A06001';
});

const isReadonlyField = (fieldName: string) =>
    resolveContactDetailFieldReadonly(fieldName, {
        isMyDetailEditableExceptName: isMyDetailEditableExceptName.value,
        isBizFileRegistrant: isBizFileRegistrant.value,
        propsReadonly: props.readonly,
        readonlyFields: props.readonlyFields,
    });

// JURISDICTION 표시 이름 (readonly일 때 사용)
const jurisdictionDisplayName = computed(() => {
    const jurisdictionCode = basicData.value.JURISDICTION;
    if (!jurisdictionCode) return '';
    const option = jurisdictionOptions.value.find(opt => opt.TOTAL_CODE === jurisdictionCode);
    return option ? option.CODE_NAME : jurisdictionCode;
});

// REPRESENTATIVE_CD 표시 이름 (readonly일 때 사용)
const representativeDisplayName = computed(() => {
    const representativeCd = basicData.value.REPRESENTATIVE_CD;
    if (!representativeCd) return '';
    const option = representativeOptions.value.find(opt => opt.CONTACT_CD === representativeCd);
    if (option) return option.CONTACT_NAME;
    if (basicData.value.REPRESENTATIVE_NAME) return basicData.value.REPRESENTATIVE_NAME;
    return representativeCd;
});

const representativeOptionsWithCurrent = computed(() => {
    const representativeCd = String(basicData.value.REPRESENTATIVE_CD || '').trim();
    const representativeName = String(basicData.value.REPRESENTATIVE_NAME || '').trim();
    if (!representativeCd || !representativeName) return representativeOptions.value;
    const exists = representativeOptions.value.some((opt) => opt.CONTACT_CD === representativeCd);
    if (exists) return representativeOptions.value;
    return [{ CONTACT_CD: representativeCd, CONTACT_NAME: representativeName }, ...representativeOptions.value];
});
</script>

<template>
    <v-card class="contact-basic-section">
        <v-card-title class="contact-section-title">
            {{ t('contact.Basic') || 'Basic' }}
        </v-card-title>
        <v-card-text>
            <v-row class="info-form-row">
                <!-- Left Column -->
                <v-col cols="12" md="6">
                    <!-- Readonly일 때는 텍스트 필드로 표시 -->
                    <v-text-field
                        v-if="isReadonlyField('JURISDICTION')"
                        :model-value="jurisdictionDisplayName"
                        :label="t('contact.Jurisdiction') || 'Jurisdiction'"
                        :readonly="true"
                        :class="{ 'readonly-field': true }"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        class="mb-4"
                    />
                    <!-- 수정 가능할 때는 autocomplete 사용 (Entity Registration Jurisdiction 스타일 정렬) -->
                    <v-autocomplete
                        v-else
                        :model-value="basicData.JURISDICTION"
                        :items="jurisdictionOptions"
                        item-title="CODE_NAME"
                        item-value="TOTAL_CODE"
                        :label="t('contact.Jurisdiction') || 'Jurisdiction'"
                        placeholder="Please Select"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        class="mb-4"
                        clearable
                        @update:model-value="(val: any) => {
                            // props.modelValue를 기반으로 업데이트
                            const currentValue = props.modelValue || {} as any;
                            const cleared = val == null || val === '';
                            const next = cleared ? null : val;
                            const updated = {
                                ...currentValue,
                                JURISDICTION: next,
                                jurisdiction: next // Create 페이지용 (Entity와 동일: null = 미선택)
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>
                
                <!-- Right Column -->
                <v-col cols="12" md="6">
                    <v-radio-group
                        :model-value="basicData.AUDITOR_YN"
                        :rules="publicAccountingEntitiesRules"
                        :readonly="readonly || readonlyFields.includes('AUDITOR_YN')"
                        inline
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('AUDITOR_YN')) return;
                            // props.modelValue를 기반으로 업데이트
                            const currentValue = props.modelValue || {} as any;
                            const auditorValue = val ?? 'No';
                            // Convert 'Yes'/'No' to boolean for auditorYn
                            const auditorYnBoolean = auditorValue === 'Yes' ? true : (auditorValue === 'No' ? false : undefined);
                            const updated = {
                                ...currentValue,
                                AUDITOR_YN: auditorValue,
                                auditorYn: auditorYnBoolean,
                                publicAccountingEntities: auditorValue // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    >
                        <template #label>
                            <span>{{ t('contact.Public Accounting Entities') || 'Public Accounting Entities' }}</span>
                        </template>
                        <v-radio :label="t('common.yes') || 'Yes'" value="Yes" />
                        <v-radio :label="t('common.no') || 'No'" value="No" />
                    </v-radio-group>
                </v-col>
                
                <!-- Left Column -->
                <v-col cols="12" md="6">
                    <v-text-field
                        :model-value="basicData.CONTACT_NAME"
                        :rules="entityNameRules"
                        :readonly="readonly || readonlyFields.includes('CONTACT_NAME')"
                        :class="{ 'readonly-field': isReadonlyField('CONTACT_NAME') }"
                        required
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            if (readonly || readonlyFields.includes('CONTACT_NAME')) return;
                            // 한글 입력 완료 후에만 업데이트 (Entity Detail 방식 참고)
                            const inputValue = event.target?.value ?? '';
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                CONTACT_NAME: inputValue,
                                contactName: inputValue, // Create 페이지용
                                entityName: inputValue // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('CONTACT_NAME')) return;
                            // props.modelValue를 기반으로 업데이트
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                CONTACT_NAME: val ?? '',
                                contactName: val ?? '', // Create 페이지용
                                entityName: val ?? '' // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    >
                        <template v-slot:label>
                            <span>{{ t('contact.Entity Name') || 'Entity Name' }} <span class="required-asterisk">*</span></span>
                        </template>
                    </v-text-field>
                </v-col>
                
                <!-- Right Column -->
                <v-col cols="12" md="6">
                    <v-text-field
                        :model-value="basicData.CONTACT_REG_NO"
                        :label="t('contact.Registration Number') || 'Registration Number'"
                        :rules="registrationNumberRules"
                        :readonly="readonly || readonlyFields.includes('CONTACT_REG_NO')"
                        :class="{ 'readonly-field': isReadonlyField('CONTACT_REG_NO') }"
                        variant="outlined"
                        density="comfortable"
                        @input="(event: any) => {
                            if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                            // 한글 입력 완료 후에만 업데이트 (Entity Detail 방식 참고)
                            const inputValue = event.target?.value ?? '';
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                CONTACT_REG_NO: inputValue,
                                contactRegNo: inputValue, // Create 페이지용
                                registrationNumber: inputValue // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                        @update:model-value="(val: any) => {
                            if (readonly || readonlyFields.includes('CONTACT_REG_NO')) return;
                            // props.modelValue를 기반으로 업데이트
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                CONTACT_REG_NO: val ?? '',
                                contactRegNo: val ?? '', // Create 페이지용
                                registrationNumber: val ?? '' // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>

                <!-- ID Type (MY 법인, 저장: ID_TYPE JSON) -->
                <v-col v-if="isJurisdictionMY" cols="12" md="6">
                    <v-select
                        :model-value="corporateIdTypeLabel"
                        :items="corporateIdTypeOptions"
                        item-title="label"
                        item-value="value"
                        label="ID Type"
                        :readonly="readonly"
                        variant="outlined"
                        density="comfortable"
                        clearable
                        @update:model-value="(val: any) => setCorporateIdType(val)"
                    />
                </v-col>

                <!-- Left Column -->
                <v-col cols="12" md="6">
                    <!-- Readonly일 때는 달력 없이 일반 텍스트 필드로 표시 -->
                    <v-text-field
                        v-if="isReadonlyField('DATE_OF_INCORPORATION')"
                        :model-value="incorporationDateDisplay"
                        :label="t('contact.Date of Incorporation') || 'Date of Incorporation'"
                        variant="outlined"
                        density="comfortable"
                        :readonly="true"
                        :class="{ 'readonly-field': true }"
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                    />
                    <!-- 수정 가능할 때는 달력 메뉴 사용 -->
                    <v-menu
                        v-else
                        v-model="incorporationDateMenu"
                        :close-on-content-click="false"
                        location="bottom"
                    >
                        <template v-slot:activator="{ props: activatorProps }">
                            <v-text-field
                                v-model="incorporationDateDisplay"
                                :label="t('contact.Date of Incorporation') || 'Date of Incorporation'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                                v-bind="activatorProps"
                            />
                        </template>
                        <v-date-picker
                            :model-value="incorporationDateCalendarDate ? new Date(incorporationDateCalendarDate + 'T00:00:00') : new Date()"
                            @update:model-value="(val: Date | string) => { 
                                let dateStr: string;
                                if (val instanceof Date) {
                                    // 로컬 시간대 기준으로 날짜 문자열 생성 (UTC 변환 방지)
                                    const year = val.getFullYear();
                                    const month = String(val.getMonth() + 1).padStart(2, '0');
                                    const day = String(val.getDate()).padStart(2, '0');
                                    dateStr = `${year}-${month}-${day}`;
                                } else {
                                    dateStr = val;
                                }
                                incorporationDate = dateStr;
                                incorporationDateCalendarDate = dateStr;
                                incorporationDateMenu = false;
                            }"
                        />
                    </v-menu>
                </v-col>
                
                <!-- Right Column -->
                <v-col cols="12" md="6">
                    <!-- Readonly일 때는 텍스트 필드로 표시 -->
                    <v-text-field
                        v-if="isReadonlyField('REPRESENTATIVE_CD')"
                        :model-value="representativeDisplayName"
                        :label="t('contact.Representative Name') || 'Representative Name'"
                        :readonly="true"
                        :class="{ 'readonly-field': true }"
                        variant="outlined"
                        density="comfortable"
                    />
                    <!-- 수정 가능할 때는 autocomplete 사용 -->
                    <v-autocomplete
                        v-else
                        :model-value="basicData.REPRESENTATIVE_CD"
                        :items="representativeOptionsWithCurrent"
                        item-title="CONTACT_NAME"
                        item-value="CONTACT_CD"
                        :label="t('contact.Representative Name') || 'Representative Name'"
                        :placeholder="t('contact.Please Select') || 'Please Select'"
                        variant="outlined"
                        density="comfortable"
                        clearable
                        @update:model-value="(val: any) => {
                            // props.modelValue를 기반으로 업데이트
                            // null, undefined, 빈 문자열 모두 null로 처리
                            const representativeValue = (val === '' || val === undefined) ? null : val;
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                REPRESENTATIVE_CD: representativeValue,
                                representativeCd: representativeValue, // Create 페이지용
                                representativeName: representativeValue // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>

                <v-col v-if="showEntityOfficerAuditorFirmNo" cols="12" md="6">
                    <v-text-field
                        :model-value="formatAuditFirmNoDisplay(entityOfficerOverlay?.firmNo)"
                        :readonly="readonly"
                        :class="{ 'readonly-field': readonly }"
                        :label="t('contact.Firm No.') || 'Firm No.'"
                        variant="outlined"
                        density="comfortable"
                        hide-details
                        @update:model-value="(val: any) => {
                            if (readonly) return;
                            const normalized = String(val ?? '').trim().replace(/^AF/i, '').trim();
                            patchEntityOfficerOverlay({ firmNo: normalized });
                        }"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped lang="scss">
@import '../scss/contact.scss';

/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-basic-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

/* Readonly 필드 스타일 (회색 배경) - Street Name과 동일한 스타일 */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled,
.readonly-field.v-select--disabled,
.readonly-field.v-autocomplete--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea),
.readonly-field.v-select--disabled :deep(.v-field),
.readonly-field.v-select--disabled :deep(.v-field__input),
.readonly-field.v-select--disabled :deep(.v-input__control),
.readonly-field.v-select--disabled :deep(.v-field__overlay),
.readonly-field.v-autocomplete--disabled :deep(.v-field),
.readonly-field.v-autocomplete--disabled :deep(.v-field__input),
.readonly-field.v-autocomplete--disabled :deep(.v-input__control),
.readonly-field.v-autocomplete--disabled :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

/* Select readonly 스타일 */
.readonly-field.v-select {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__input) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-input__control) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.readonly-field.v-select :deep(.v-field__overlay) {
    pointer-events: none !important;
    cursor: not-allowed !important;
}

.required-asterisk {
    color: #F44336 !important;
    font-weight: bold;
    margin-left: 2px;
}
</style>